import type { AxiosResponse } from 'axios';
import type { ability } from '@oliasoft/authorization-ui';
import httpClient from '~utils/httpClient';
import { API_ENDPOINTS } from '~modules/Auth/constants';
import {
  IApplicationRole,
  IBulkRoleAssignmentPayload,
  IDeleteRolePayload,
  IRolePayload,
  IRoleAssignmentPayload,
  IRolesData,
  IRoleToSync,
  ISelectedRoleToSync,
} from './interfaces';

export type TAbilityRules = Parameters<typeof ability.update>[0];

export function getRoles(applicationId?: number | string) {
  return httpClient<IRolesData>({
    method: 'get',
    url: `${API_ENDPOINTS.roles}/${applicationId}`,
    baseURL: '/',
  });
}

export function createRole(data: IRolePayload) {
  return httpClient({
    method: 'post',
    url: `${API_ENDPOINTS.createRole}?applicationId=${data.applicationId}`,
    baseURL: '/',
    data,
  });
}

export function updateRole(data: IRolePayload) {
  return httpClient({
    method: 'patch',
    url: `${API_ENDPOINTS.role}/${data.roleId}`,
    baseURL: '/',
    data,
  });
}

export function deleteRole({ roleId }: IDeleteRolePayload) {
  return httpClient({
    method: 'delete',
    url: `${API_ENDPOINTS.role}/${roleId}`,
    baseURL: '/',
  });
}

export function assignRole(data: IRoleAssignmentPayload) {
  return httpClient<{ msg: string }>({
    method: 'post',
    url: `${API_ENDPOINTS.assignRole}`,
    baseURL: '/',
    data,
  });
}

export function assignRoleBulk(data: IBulkRoleAssignmentPayload) {
  return httpClient<{ msg: string }>({
    method: 'post',
    url: `${API_ENDPOINTS.assignRoleBulk}`,
    baseURL: '/',
    data,
  });
}

export function unAssignRole(data: IRoleAssignmentPayload) {
  return httpClient<{ msg: string }>({
    method: 'post',
    url: `${API_ENDPOINTS.unAssignRole}`,
    baseURL: '/',
    data,
  });
}

export function getSubjects(data?: Record<string, unknown>) {
  return httpClient({
    method: 'get',
    url: `${API_ENDPOINTS.subjects}`,
    baseURL: '/',
    data,
  });
}

export function getApplicationRoles(all?: boolean) {
  const url = all ? API_ENDPOINTS.applicationRoles : API_ENDPOINTS.applicationRolesForCustomer;
  return httpClient<IApplicationRole[]>({
    method: 'get',
    url,
    baseURL: '/',
  });
}

export function getUserPermissions(): Promise<AxiosResponse<TAbilityRules>> {
  return httpClient<TAbilityRules>({
    method: 'get',
    url: '/api/authorization',
    baseURL: '/',
  });
}

export function synchronizeRole(role: ISelectedRoleToSync | null) {
  return httpClient({
    method: 'post',
    url: API_ENDPOINTS.synchronizeRole,
    baseURL: '/',
    data: role,
  });
}

export function getRolesForSync({ applicationName }: { applicationName: string }) {
  return httpClient<IRoleToSync[]>({
    method: 'get',
    url: `${API_ENDPOINTS.rolesForSync}/${applicationName}`,
    baseURL: '/',
  });
}

export function duplicateRole(roleId: number) {
  return httpClient({
    method: 'post',
    url: `${API_ENDPOINTS.duplicateRole}/${roleId}`,
    baseURL: '/',
    data: {},
  });
}
